import { supabase } from "@/integrations/supabase/client";
import { getDeviceId } from "@/lib/deviceId";
import { Capacitor } from "@capacitor/core";
import { logger } from "@/lib/logger";

interface ReminderTime {
  time: string;
  enabled: boolean;
}

interface Prescription {
  id: string;
  medication_name: string;
  dosage: string;
  reminders: any;
}

/**
 * Global medication reminder initializer
 * 
 * This verifies that the user has a deliverable device registered
 * for server-side push notifications. The actual reminders are sent
 * by the edge function (send-medication-reminders) on a cron schedule.
 * 
 * Client-side fallback is only for PWA/browser when server push isn't available.
 */
export const initializeGlobalMedicationScheduler = async () => {
  try {
    logger.log('[Global Scheduler] Checking notification status...');

    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      logger.log('[Global Scheduler] No user logged in');
      return;
    }

    // Check if user has a deliverable device registered
    const { data, error } = await supabase.rpc('get_notification_devices_summary', {
      p_user_id: user.id
    });

    if (error) {
      console.error('[Global Scheduler] Error checking devices:', error);
      return;
    }

    let row = data?.[0];
    let deliverableCount = row?.deliverable_count || 0;
    let needsFixCount = row?.needs_fix_count || 0;

    // Auto-purge stale invalid/unknown device rows so users don't sit forever
    // with "needs fix" flags from previous installs or expired subscriptions.
    if (needsFixCount > 0) {
      const { data: deletedCount, error: cleanupError } = await supabase.rpc(
        'cleanup_stale_notification_devices',
        { p_user_id: user.id }
      );
      if (cleanupError) {
        console.warn('[Global Scheduler] Cleanup RPC failed:', cleanupError);
      } else if ((deletedCount ?? 0) > 0) {
        console.log(`[Global Scheduler] 🧹 Purged ${deletedCount} stale device row(s)`);
        // Re-check after cleanup
        const { data: refreshed } = await supabase.rpc('get_notification_devices_summary', {
          p_user_id: user.id,
        });
        row = refreshed?.[0];
        deliverableCount = row?.deliverable_count || 0;
        needsFixCount = row?.needs_fix_count || 0;
      }
    }

    if (deliverableCount > 0) {
      console.log(`[Global Scheduler] ✅ User has ${deliverableCount} deliverable device(s) - server will send reminders`);
      return;
    }

    if (needsFixCount > 0) {
      console.log(`[Global Scheduler] ⚠️ User has ${needsFixCount} device(s) needing fix`);
      return;
    }

    // No devices registered - check if we should prompt for setup
    const deviceId = await getDeviceId();
    console.log('[Global Scheduler] No devices registered for user. Device ID:', deviceId.substring(0, 8) + '...');

    // For browser mode (not installed PWA), we can use client-side fallback
    // but only if permissions are already granted
    if (!Capacitor.isNativePlatform() && !window.matchMedia('(display-mode: standalone)').matches) {
      if ('Notification' in window && Notification.permission === 'granted') {
        console.log('[Global Scheduler] Browser mode with permissions - using lightweight client fallback');
        // Client-side fallback is handled by PWAPushNotifications class automatically
      }
    }
  } catch (error) {
    console.error('[Global Scheduler] Error initializing:', error);
  }
};

/**
 * Re-check device status (call this after login or when reminders are updated)
 */
export const rescheduleAllMedicationReminders = initializeGlobalMedicationScheduler;
